import {getUserId, isSuperUser} from '@Libraries/user.library';
import {getURISegment} from '@Utils/url.util';
import type {Component, Editor} from 'grapesjs';
import type {ImageData} from '@Libraries/add-media-library';
import {getAssetUrl} from '@Utils/s3.util';
import PMWStepButtons from './vos/pmwstepbuttons';
import PMWSliderComponentVO from './vos/pmwslidervo';
import PresetComponentVO from './vos/presetcompvo';
import {ExtendedControlIcons} from '@/email-maker/grapesjs-email-editor-plugin/src/vos/extendedcontrolicons';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src/index';
import type {
  AutoBrandingOptions,
  DropdownSelectorType,
  AiRewritePromptBoxType,
  GenericStyleOptions,
  ColorStyleType,
  CTAButtonsType,
  CTAButtonType,
  FontSelectorType,
  IconButtonType,
  InfoBoxType,
  ListType,
  MediaModificationListType,
  StyleHeading,
  SuperUserOptions,
  UploadType,
} from './styles.types';
import type {ComponentAlignmentStyle} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {BUTTON_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX, TEXT_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';

// Type alias to help with GrapesJS property compatibility
type PropertyTypes = any;

/**
 * StyleManager functions
 */
export default (opt: PrimaryConfigOptions): void => {
  const SU_OPTS: SuperUserOptions = {};
  const adminFeaturesUserIdWhiteList: number[] = [12118747]; // Ric's friend who needs access to the import HTML feature
  window.grapesjsMeta = {};
  const {editor} = opt;
  const widthSliderVO = new PMWSliderComponentVO('width', window.i18next.t('pmwjs_width'), '%', 1, 100, {defaults: '100%'});
  const limitedWidthSliderVO = new PMWSliderComponentVO('width', window.i18next.t('pmwjs_width'), '%', 80, 100, {defaults: '80%'});
  const paddingSliderVO = new PMWSliderComponentVO('padding', window.i18next.t('pmwjs_spacing'), 'px', 0, 50, {
    defaults: 0,
    extendedControls: true,
  });
  const borderRadiusSliderVO = new PMWSliderComponentVO('border-radius', window.i18next.t('pmwjs_roundness'), 'px', 0, 100, {
    defaults: 0,
    extendedControls: true,
    extendedControlIcons: new ExtendedControlIcons('icon-corner-top-right', 'icon-corner-bottom-right', 'icon-corner-bottom-left', 'icon-corner-top-left'),
  });
  const borderWidthSliderVO = new PMWSliderComponentVO('border-width', window.i18next.t('pmwjs_thickness'), 'px', 0, 20, {
    defaults: 0,
  });
  const opacitySliderVO = new PMWSliderComponentVO('opacity', window.i18next.t('pmwjs_opacity'), '%', 5, 100, {defaults: 100});
  const columnsStepButtons = new PMWStepButtons('pmw-columns', window.i18next.t('pmwjs_columns'), '', 1, 4, {defaults: 1});
  const rowStepButtons = new PMWStepButtons('pmw-rows', window.i18next.t('pmwjs_rows'), '', 1, 20, {defaults: 1});
  const cellBorderWidthSliderVO = new PMWSliderComponentVO('pmw-cell-border-width', window.i18next.t('pmwjs_border'), 'px', 0, 20, {toggleProp: 'pmw-cell-border-color'});
  const cellBorderRadiusSliderVO = new PMWSliderComponentVO('pmw-cell-border-radius', window.i18next.t('pmwjs_cell_roundness'), 'px', 0, 100);
  const cellSpacingSliderVO = new PMWSliderComponentVO('pmw-cell-spacing', window.i18next.t('pmwjs_cell_spacing'), 'px', 0, 20);
  const columnSpacingSliderVO = new PMWSliderComponentVO('pmw-cell-spacing', window.i18next.t('pmwjs_gap'), '', 0, 20);
  const fontSizeSliderVO = new PMWSliderComponentVO('font-size', window.i18next.t('pmwjs_font_size'), 'px', 4, 60, {defaults: '16px'});
  const lineHeightSliderVO = new PMWSliderComponentVO('line-height', window.i18next.t('pmwjs_line_height'), 'px', 0, 60, {defaults: '16px'});
  const heightSliderVO = new PMWSliderComponentVO('height', window.i18next.t('pmwjs_height'), 'px', 0, 80, {defaults: '80px'});
  const letterSpacingSliderVO = new PMWSliderComponentVO('letter-spacing', window.i18next.t('pmwjs_letter_spacing'), 'px', 0, 30, {defaults: '0px'});
  const verticalMarginSliderVO = new PMWSliderComponentVO('margin', window.i18next.t('pmwjs_spacing'), 'px', 0, 50, {
    defaults: '0px',
    extendedControls: true,
  });
  const socialIconGapSliderVO = new PMWSliderComponentVO('pmw-icon-margin', window.i18next.t('pmwjs_icon_spacing'), 'px', 0, 50, {defaults: '0px'});
  const socialIconSizeSliderVO = new PMWSliderComponentVO('pmw-icon-size', window.i18next.t('pmwjs_icon_size'), 'px', 0, 80, {defaults: '38px'});
  const COLUMNS_SELECTOR = {
    name: window.i18next.t('pmwjs_columns'),
    property: window.PMW.emails.props.COLUMNS_PRESET,
    type: 'pmw-columns-selector',
    component: undefined,
    previewIdFixedList: [2, 8, 11],
    initalSelectedId: 1,
    list: editor.runCommand(window.PMW.emails.commands.GET_SECTION_COLUMNS_PRESETS) as PresetComponentVO[],
  };
  const PRESET_SELECTOR = {
    property: window.PMW.emails.props.COMPONENT_PRESET,
    type: window.PMW.emails.styles.ID_PRESET_SELECTOR,
    component: undefined,
    default: '0',
    callback(comp: Component, presetCompVO: PresetComponentVO): void {
      const radius = `${presetCompVO.compValues[0]}${presetCompVO.classUnit}`;
      editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {property: 'border-radius', value: radius, component: comp});
    },
    list: [
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 1, [window.PMW.emails.buttonPresets[1]], 'px'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 2, [window.PMW.emails.buttonPresets[2]], 'px'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 3, [window.PMW.emails.buttonPresets[3]], 'px'),
      new PresetComponentVO(window.PMW.emails.props.COMPONENT_PRESET, 4, [window.PMW.emails.buttonPresets[4]], 'px'),
    ],
  };
  const isEditorAiContentGenerationEnabled = $('#html-editor-container').attr('data-editor-ai-content-generation-enabled') === '1';
  const imageAndDesignReplacementOptions = {
    iconClass: 'icon-replace',
    label: window.i18next.t('pmwjs_replace'),
    subOptions: [
      {
        label: window.i18next.t('pmwjs_myuploads'),
        icon: 'icon-upload',
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_MY_UPLOADS_IMAGE);
        },
      },
      {
        label: window.i18next.t('pmwjs_explore_media'),
        icon: 'icon-media',
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_EXPLORE_MEDIA_IMAGE);
        },
      },
      {
        label: window.i18next.t('pmwjs_ai_images'),
        icon: 'icon-ai-image',
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_AI_IMAGES);
        },
      },
      {
        label: window.i18next.t('pmwjs_my_designs'),
        icon: 'icon-designs',
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.OPEN_DESIGN_SELECTOR, {isMultiSelectAllowed: false, replaceComponent: true});
        },
      },
    ],
    callback: (_editorInstance: Editor, _component: Component, _li: HTMLElement, modList: HTMLElement): void => {
      const modListContainer = modList.querySelector('.mod-list-container');
      const modListSuboptions = modList.querySelector('.mod-list-suboptions-container');
      modListContainer?.classList.add('_hidden');
      modListSuboptions?.classList.remove('_hidden');
    },
  };

  const updateSectorTitle = (title: string): void => {
    editor.runCommand(window.PMW.emails.commands.UPDATE_SECTOR_TITLE, {title});
  };

  const hideTitleForWrapperSector = (component: Component): void => {
    const header = $('.email-editor-mobile-style-header');
    const headerContent = $('.email-editor-header-content');
    const mobileHandlebar = $('.mobile-handlebar');
    const styleManagerContainer = $('.html-editor-style-manager');

    if (component.is(window.PMW.emails.components.container.PMW_WRAPPER)) {
      headerContent.addClass('_hidden');
      header.addClass('height-auto spacing-p-0 hidden-desktop');
      mobileHandlebar.addClass('spacing-m-2');
      styleManagerContainer.addClass('-no-header');
    } else {
      headerContent.removeClass('_hidden');
      header.removeClass('height-auto spacing-p-0 hidden-desktop');
      mobileHandlebar.removeClass('spacing-m-2');
      styleManagerContainer.removeClass('-no-header');
    }
  };

  const hidePropertiesForApprovedTemplates = (editorInstance: Editor): void => {
    const isLoadMode = getURISegment(1) === window.PMW.emails.MODE_LOAD;
    const isTemplateApproved = $('#html-editor-container').attr('data-is-approved') === '1';

    if (!isTemplateApproved || !isLoadMode) {
      return;
    }

    const isWrapperSelected = editorInstance.getSelected()?.is(window.PMW.emails.components.container.PMW_WRAPPER);

    if (isWrapperSelected) {
      $(`.js-field-input-${window.PMW.emails.props.TEMPLATE_NAME}`).attr('disabled', 'true');
    }
  };

  const SU_GLOBAL_SELECTION: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'any selection: disabled',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (_editorInstance, _selected: Component, btnEl: HTMLElement): void => {
      SU_OPTS.selectionEnabled = !SU_OPTS.selectionEnabled;

      const jsCtaText = btnEl.querySelector('.js-cta-text');

      if (jsCtaText) {
        jsCtaText.innerHTML = `any selection: ${SU_OPTS.selectionEnabled ? 'enabled' : 'disabled'}`;
      }

      SU_GLOBAL_SELECTION.text = `any selection: ${SU_OPTS.selectionEnabled ? 'enabled' : 'disabled'}`;
      SU_OPTS.selectionMap = {};

      if (SU_OPTS.selectionEnabled) {
        editor.getWrapper()?.onAll((comp: Component) => {
          if (SU_OPTS.selectionMap) {
            SU_OPTS.selectionMap[comp.cid] = comp.get('selectable') || false;
          }
          comp.set('selectable', true);
        });
      } else {
        editor.getWrapper()?.onAll((comp) => {
          if (typeof SU_OPTS.selectionMap?.[comp.cid] !== 'undefined') {
            comp.set('selectable', SU_OPTS.selectionMap[comp.cid]);
          }
        });
      }
    },
  };

  const WIDTH_DROPDOWN_SELCTOR: DropdownSelectorType = {
    type: window.PMW.emails.styles.ID_DROPDOWN_STYLE_SELECTOR,
    property: 'button-width',
    component: undefined,
    label: window.i18next.t('pmwjs_width'),
    defaultDropDownValue: 'auto',
    dropdownOptions: [
      {text: window.i18next.t('pmwjs_auto'), value: 'auto'},
      {text: window.i18next.t('pmwjs_custom'), value: 'custom'},
    ],
    toggleProps: ['width'],
    hideToggledPropsStyleValue: 'auto',
    onDropDownItemChange: (selected: Component, _property: string, propertyValue: string): void => {
      if (propertyValue === 'auto') {
        editor.runCommand(window.PMW.emails.commands.SET_AUTO_WIDTH_STYLE, {component: selected});
      } else if (propertyValue === 'custom') {
        editor.runCommand(window.PMW.emails.commands.SET_CUSTOM_WIDTH_STYLE, {component: selected});
      }
    },
  };

  const COPY_PASTE_STYLES: CTAButtonsType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    buttons: [
      {
        text: window.i18next.t('pmwjs_copy'),
        type: window.PMW.emails.styles.ID_CTA_BUTTON,
        iconClass: 'icon-copy-styles',
        btnClass: 'secondary -icon-left',
        component: undefined,
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.COPY_STYLES);
        },
      },
      {
        text: window.i18next.t('pmwjs_paste'),
        type: window.PMW.emails.styles.ID_CTA_BUTTON,
        iconClass: 'icon-paste-styles',
        btnClass: 'js-paste-styles secondary -icon-left -disabled',
        component: undefined,
        cb: (): void => {
          editor.runCommand(window.PMW.emails.commands.PASTE_STYLES);
        },
      },
    ],
  };
  const HORIZONTAL_ALIGN: ListType = {
    property: 'horizontal-align',
    type: window.PMW.emails.styles.ID_LIST,
    component: undefined,
    title: window.i18next.t('pmwjs_horizontal_alignment'),
    defaults: '',
    list: [
      {
        value: 'left',
        name: window.i18next.t('pmwjs_left'),
        classes: 'icon-h-align-left',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.H_ALIGN_COMPONENT_LEFT);
        },
      },
      {
        value: 'center',
        name: window.i18next.t('pmwjs_center'),
        classes: 'icon-h-align-center',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.H_ALIGN_COMPONENT_CENTER);
        },
      },
      {
        value: 'right',
        name: window.i18next.t('pmwjs_right'),
        classes: 'icon-h-align-right',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.H_ALIGN_COMPONENT_RIGHT);
        },
      },
    ],
  };
  const TEXT_ALIGNMENT_LIST = [
    {
      value: 'left',
      name: window.i18next.t('pmwjs_left'),
      classes: 'icon-align-left',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_LEFT);
      },
    },
    {
      value: 'center',
      name: window.i18next.t('pmwjs_center'),
      classes: 'icon-align-center',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_CENTER);
      },
    },
    {
      value: 'right',
      name: window.i18next.t('pmwjs_right'),
      classes: 'icon-align-right',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_RIGHT);
      },
    },
  ];
  const TEXT_ALIGNMENT_SIMPLE_LIST = [
    {
      value: 'left',
      name: window.i18next.t('pmwjs_left'),
      classes: 'icon-h-align-left',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_LEFT);
      },
    },
    {
      value: 'center',
      name: window.i18next.t('pmwjs_center'),
      classes: 'icon-h-align-center',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_CENTER);
      },
    },
    {
      value: 'right',
      name: window.i18next.t('pmwjs_right'),
      classes: 'icon-h-align-right',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_RIGHT);
      },
    },
  ];
  const TEXT_ALIGN_SIMPLE: ListType = {
    property: 'text-align',
    title: window.i18next.t('pmwjs_alignment'),
    type: window.PMW.emails.styles.ID_LIST,
    component: undefined,
    defaults: '',
    list: TEXT_ALIGNMENT_SIMPLE_LIST,
  };
  const TEXT_ALIGN: ListType = {
    property: 'text-align',
    title: window.i18next.t('pmwjs_alignment'),
    type: window.PMW.emails.styles.ID_LIST,
    component: undefined,
    defaults: '',
    list: TEXT_ALIGNMENT_LIST.concat({
      value: 'justify',
      name: window.i18next.t('pmwjs_justify'),
      classes: 'icon-align-block',
      callback: (): void => {
        editor.runCommand(window.PMW.emails.commands.text.ALIGN_JUSTIFIED);
      },
    }),
  };
  const VERTICAL_ALIGN: ListType = {
    property: 'vertical-align',
    title: window.i18next.t('pmwjs_vertical_alignment'),
    type: window.PMW.emails.styles.ID_LIST,
    component: undefined,
    defaults: '',
    list: [
      {
        value: 'top',
        name: window.i18next.t('pmwjs_top'),
        classes: 'icon-vertical-top',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.V_ALIGN_COMPONENT_TOP);
        },
      },
      {
        value: 'middle',
        name: window.i18next.t('pmwjs_middle'),
        classes: 'icon-v-align-center',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.V_ALIGN_COMPONENT_MIDDLE);
        },
      },
      {
        value: 'bottom',
        name: window.i18next.t('pmwjs_bottom'),
        classes: 'icon-vertical-bottom',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.V_ALIGN_COMPONENT_BOTTOM);
        },
      },
    ],
  };
  const FONT_STYLE_OPTIONS: ListType = {
    property: 'pmw-font-style',
    type: window.PMW.emails.styles.ID_LIST,
    component: undefined,
    title: window.i18next.t('pmwjs_style'),
    toggleable: true,
    defaults: '',
    list: [
      {
        property: 'font-weight',
        value: 'bold',
        name: 'Bold',
        classes: 'icon-bold',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLE_BOLD);
        },
      },
      {
        property: 'font-style',
        value: 'italic',
        name: 'Italics',
        classes: 'icon-italic',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLE_ITALIC);
        },
      },
      {
        property: 'text-decoration',
        value: 'line-through',
        name: 'Strike-through',
        classes: 'icon-strike-through',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLE_STRIKETHROUGH);
        },
      },
      {
        property: 'text-decoration',
        value: 'underline',
        name: 'Underline',
        classes: 'icon-underline',
        callback: (): void => {
          editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLE_UNDERLINE);
        },
      },
    ],
  };
  const TEXT_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: 'color',
    component: undefined,
    label: window.i18next.t('pmwjs_text_color'),
    isDeletable: false,
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };
  const LINK_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: window.PMW.emails.styles.LINK_COLOR,
    component: undefined,
    label: window.i18next.t('pmwjs_link_color'),
    isDeletable: false,
    onColorChange: (value: string): void => {
      editor.runCommand(window.PMW.emails.commands.UPDATE_LINKS_COLOR, {color: value, component: editor.getSelected()});
    },
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };
  const BG_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: 'background-color',
    component: undefined,
    label: window.i18next.t('pmwjs_background_color'),
    isDeletable: true,
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };

  const BORDER_SELECTOR: DropdownSelectorType = {
    type: window.PMW.emails.styles.ID_DROPDOWN_STYLE_SELECTOR,
    property: 'border-style',
    component: undefined,
    label: window.i18next.t('pmwjs_border'),
    defaultDropDownValue: 'none',
    toggleProps: ['border-width', 'border-color'],
    hideToggledPropsStyleValue: 'none',
    dropdownOptions: [
      {text: window.i18next.t('pmwjs_none'), value: 'none'},
      {text: window.i18next.t('pmwjs_background_solid'), value: 'solid'},
      {text: window.i18next.t('pmwjs_dashed'), value: 'dashed'},
    ],
    onDropDownItemChange: (selected: Component, property: string, propertyValue: string): void => {
      editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
        component: selected,
        property,
        value: propertyValue,
      });
    },
  };

  const ICON_STYLE: DropdownSelectorType = {
    type: window.PMW.emails.styles.ID_DROPDOWN_STYLE_SELECTOR,
    property: 'icon-style',
    component: undefined,
    label: window.i18next.t('pmwjs_icon_style'),
    defaultDropDownValue: 'original',
    dropdownOptions: [
      {text: window.i18next.t('pmwjs_original'), value: 'original', icon: getAssetUrl('social-icons/original/social-icon-facebook.png')},
      {text: window.i18next.t('pmwjs_square'), value: 'square-black', icon: getAssetUrl('social-icons/square-black/social-icon-facebook.png')},
      {text: window.i18next.t('pmwjs_circle'), value: 'circle-black', icon: getAssetUrl('social-icons/circle-black/social-icon-facebook.png')},
      {text: window.i18next.t('pmwjs_line'), value: 'line-black', icon: getAssetUrl('social-icons/line-black/social-icon-facebook.png')},
      {text: window.i18next.t('pmwjs_dark'), value: 'line-white', icon: getAssetUrl('social-icons/line-white/social-icon-facebook.png')},
    ],
    onDropDownItemChange: (_selected: Component, _property: string, propertyValue: string): void => {
      editor.runCommand(window.PMW.emails.commands.UPDATE_SOCIAL_LINK_ICON_STYLE, {iconStyle: propertyValue});
      editor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
    },
  };

  const BORDER_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: 'border-color',
    component: undefined,
    default: '#FFFFFF',
    label: window.i18next.t('pmwjs_color'),
    isDeletable: true,
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };
  const CELLS_BG_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: 'pmw-cell-background-color',
    component: undefined,
    label: window.i18next.t('pmwjs_color'),
    default: '#FAFAFA',
    onColorChange: (value: string): void => {
      editor.runCommand(window.PMW.emails.commands.UPDATE_CELLS_BACKGROUND_COLOR, {color: value});
    },
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };
  const CELLS_BORDER_COLOR: ColorStyleType = {
    type: window.PMW.emails.styles.ID_COLOR_PICKER,
    property: 'pmw-cell-border-color',
    component: undefined,
    label: window.i18next.t('pmwjs_border_color'),
    default: '#242424',
    onColorChange: (value: string): void => {
      editor.runCommand(window.PMW.emails.commands.UPDATE_CELLS_BORDER_COLOR, {color: value});
    },
    isDeletable: true,
    swatchesHeading: window.i18next.t('pmwjs_on_my_email'),
  };

  const EMAIL_TEMPLATE_STYLES: AutoBrandingOptions = {
    type: window.PMW.emails.styles.ID_AUTO_BRANDING,
    property: 'pmw-auto-branding',
    component: undefined,
  };

  const MEGRE_TAGS_STYLES: GenericStyleOptions = {
    type: window.PMW.emails.styles.ID_MERGE_TAGS,
    property: 'pmw-merge-tags',
    component: undefined,
  };

  const SU_UPLOAD_CUSTOM_HTML: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Upload custom HTML',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: () => {
      editor.runCommand(window.PMW.emails.commands.OPEN_CUSTOM_HTML_DIALOG);
    },
  };

  const SU_OPEN_IN_ADMIN_PANEL: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Open admin tools',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (): void => {
      if (window.PMW.idEmailTemplate) {
        window.open(window.PMW.util.site_url(`emailmaker/openTemplateTools/${window.PMW.idEmailTemplate}`));
      } else {
        alert('The email template has no ID. Has it been saved yet?');
      }
    },
  };

  const SU_IMAGE_INFO: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Check image sources',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      editorInstance.runCommand(window.PMW.emails.commands.OPEN_COMPONENT_INFO_MODAL);
    },
  };

  const SU_COPY_EMAIL_HTML_MARKETING: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Copy HTML for Marketing',
    iconClass: 'icon-copy',
    btnClass: 'secondary -icon-left',
    cb: async (editorInstance) => {
      await editorInstance.runCommand(window.PMW.emails.commands.COPY_HTML_FOR_MARKETING_EMAIL);
      /*
      Chrome bug workaround. Alerting right after a clipboard copy takes the focus away,
      and ends up not copying
       */
      setTimeout(() => {
        alert('Copied!');
      }, 500);
    },
  };

  const SU_COPY_EMAIL_TEXT_MARKETING: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Copy TEXT for Marketing',
    iconClass: 'icon-copy',
    btnClass: 'secondary -icon-left',
    cb: async (editorInstance) => {
      await editorInstance.runCommand(window.PMW.emails.commands.COPY_TEXT_FOR_MARKETING_EMAIL);
      /*
      Chrome bug workaround. Alerting right after a clipboard copy takes the focus away,
      and ends up not copying
       */
      setTimeout(() => {
        alert('Copied!');
      }, 500);
    },
  };

  const SU_COPY_EMAIL_MEDIA_QUERIES_MARKETING: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Copy Media Queries for Marketing',
    iconClass: 'icon-copy',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      editorInstance.runCommand(window.PMW.emails.commands.COPY_MEDIA_QUERIES_FOR_MARKETING_EMAIL);
      /*
      Chrome bug workaround. Alerting right after a clipboard copy takes the focus away,
      and ends up not copying
       */
      setTimeout(() => {
        alert('Copied!');
      }, 500);
    },
  };

  const COPY_TEXT_JSON: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Copy text JSON',
    iconClass: 'icon-copy',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      editorInstance.runCommand(window.PMW.emails.commands.COPY_TEXT_JSON);
      /*
      Chrome bug workaround. Alerting right after a clipboard copy takes the focus away,
      and ends up not copying
       */
      setTimeout(() => {
        alert('Copied!');
      }, 500);
    },
  };

  const SU_GLOBAL_TEST: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'test',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cb: (_editorInstance, _selected: Component, _btnEl: HTMLElement, _e: Event): void => {},
  };

  const SU_GLOBAL_LOG_JSON: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'log JSON',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      if (window.grapesjsMeta) {
        window.grapesjsMeta.pmwEmailJson = editorInstance.getProjectData();
        console.info('saved to window.grapesjsMeta.pmwEmailJson:', window.grapesjsMeta.pmwEmailJson);
      }
    },
  };

  const SU_GLOBAL_AUTOSAVE: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Toggle autosave',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (_editorInstance, _selected, btnEl): void => {
      let isEnabled: boolean = editor.StorageManager.isAutosave();
      editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: !isEnabled});
      isEnabled = editor.StorageManager.isAutosave();

      const jsCtaText = btnEl.querySelector('.js-cta-text');

      if (jsCtaText) {
        jsCtaText.innerHTML = `autosave: ${isEnabled ? 'enabled' : 'disabled'}`;
      }

      SU_GLOBAL_AUTOSAVE.text = `autosave: ${isEnabled ? 'enabled' : 'disabled'}`;
    },
  };

  const SU_FIX_WRAPPER_WIDTH: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Fix wrapper width',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      editorInstance.runCommand(window.PMW.emails.commands.FIX_WRAPPER_WIDTH);
    },
  };

  const SU_GLOBAL_EXPOSE_EDITOR: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Expose editor',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance): void => {
      if (window.grapesjsMeta) {
        window.grapesjsMeta.gjsEditor = editorInstance;
        console.info('exposed editor under window as window.grapesjsMeta.gjsEditor');
      }
    },
  };

  const SU_DESKTOP_PREVIEW: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Desktop preview',
    iconClass: 'icon-download',
    btnClass: 'secondary -icon-left',
    cb: (): void => {
      window.open(window.PMW.util.site_url(`emailmaker/downloadEmailTemplatePreview/${window.PMW.idEmailTemplate}?size=desktop`), '_blank');
    },
  };

  const SU_TABLET_PREVIEW: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Tablet preview',
    iconClass: 'icon-download',
    btnClass: 'secondary -icon-left',
    cb: (): void => {
      window.open(window.PMW.util.site_url(`emailmaker/downloadEmailTemplatePreview/${window.PMW.idEmailTemplate}?size=tablet`), '_blank');
    },
  };

  const SU_PHONE_PREVIEW: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Phone preview',
    iconClass: 'icon-download',
    btnClass: 'secondary -icon-left',
    cb: (): void => {
      window.open(window.PMW.util.site_url(`emailmaker/downloadEmailTemplatePreview/${window.PMW.idEmailTemplate}?size=phone`), '_blank');
    },
  };

  const SU_COMPONENT_TEST: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'test',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    cb: (): void => {},
  };

  const SU_COMPONENT_LOG_PROPS: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Log properties',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (_editorInstance, selected: Component): void => {
      console.info(selected.props());
    },
  };

  const SU_COMPONENT_REMOVE: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'remove component',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (_editorInstance, selected: Component): void => {
      selected.remove();
    },
  };

  const SU_PARENT_REMOVE: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'remove parent',
    iconClass: 'icon-screen-rocket',
    btnClass: 'secondary -icon-left',
    cb: (_editorInstance, selected: Component): void => {
      selected.parent()?.remove();
    },
  };

  const SU_SAVE_AS_SECTION_TEMPLATE: CTAButtonType = {
    type: window.PMW.emails.styles.ID_CTA_BUTTON,
    text: 'Save as section template',
    iconClass: 'icon-save',
    btnClass: 'secondary -icon-left',
    cb: (editorInstance, selected: Component): void => {
      editorInstance.runCommand(window.PMW.emails.commands.SAVE_COMPONENT_TEMPLATE, {component: selected});
    },
  };

  const BACKGROUND_MEDIA: UploadType = {
    property: 'background-image',
    label: window.i18next.t('pmwjs_background_media'),
    type: window.PMW.emails.styles.ID_UPLOAD_BUTTON,
    component: undefined,
    onClick(cb: VoidFunction): void {
      const selected = editor.getSelected();

      if (!selected) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.OPEN_MEDIA_MODAL, {
        onStartAddSelected: () => {
          editor.runCommand(window.PMW.emails.commands.SET_IMAGE_LOADING, {
            component: selected,
            loading: true,
            text: window.i18next.t('pmwjs_loading'),
          });
        },
        onAddSelected: (mediaItems: ImageData[]) => {
          const videoExtensions = ['mp4', 'webm', 'mov'];
          const mediaType = videoExtensions.includes(mediaItems[0].extension) ? window.PMW.emails.mediaType.VIDEO : window.PMW.emails.mediaType.IMAGE;

          selected.set(window.PMW.emails.props.MEDIA_TYPE, mediaType);
          selected.set(window.PMW.emails.props.HAS_BACKGROUND_MEDIA, true);

          editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
            callback: cb,
            isBackground: true,
            component: selected,
            source: mediaItems[0].source,
            extension: mediaItems[0].extension,
            hashedFilename: mediaItems[0].hashedFilename,
          });
        },
      });
    },
    onClear(): void {
      const selected = editor.getSelected();
      if (!selected) {
        return;
      }

      selected.set(window.PMW.emails.props.MEDIA_TYPE, undefined);
      selected.set(window.PMW.emails.props.HAS_BACKGROUND_MEDIA, false);

      editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
        property: 'background-image',
        value: '',
      });
      selected.removeAttributes(['background']);
    },
  };
  const FONT_FAMILY: FontSelectorType = {
    label: window.i18next.t('pmwjs_font'),
    property: 'font-family',
    type: 'pmw-font-selector',
    component: undefined,
    defaults: {id: 'helvetica', value: 'Helvetica, sans-serif', name: 'Helvetica'},
    list: [
      {id: 'arial', value: 'Arial, Helvetica, sans-serif', name: 'Arial'},
      {id: 'arial-black', value: 'Arial Black', name: 'Arial Black'},
      {id: 'brush-script-mt', value: 'Brush Script MT, sans-serif', name: 'Brush Script MT'},
      {id: 'comic-sans-ms', value: 'Comic Sans MS, cursive, sans-serif', name: 'Comic Sans MS'},
      {id: 'courier-new-courer', value: 'Courier New Courier, monospace', name: 'Courier New Courier'},
      {id: 'georgia', value: 'Georgia, serif', name: 'Georgia'},
      {id: 'helvetica', value: 'Helvetica, sans-serif', name: 'Helvetica'},
      {id: 'impact', value: 'Impact, Charcoal, sans-serif', name: 'Impact'},
      {id: 'lucida-sans-unicode', value: 'Lucida Sans Unicode, Lucida Grande, sans-serif', name: 'Lucida Sans Unicode'},
      {id: 'source-sans-pro', value: 'Source Sans Pro, Helvetica, Arial', name: 'Source Sans Pro'},
      {id: 'tahoma', value: 'Tahoma, Geneva, sans-serif', name: 'Tahoma'},
      {id: 'times-new-roman', value: 'Times New Roman, Times, serif', name: 'Times New Roman'},
      {id: 'trebuchet-ms', value: 'Trebuchet MS, Helvetica, sans-serif', name: 'Trebuchet MS'},
      {id: 'verdana', value: 'Verdana, Geneva, sans-serif', name: 'Verdana'},
    ],
  };
  const VIDEO_WARNING: InfoBoxType = {
    type: window.PMW.emails.styles.ID_INFO_BOX,
    component: undefined,
    class: '-warning no-vertical-margin',
    content: window.i18next.t('pmwjs_video_trimmed_warning'),
    isVisible: () => true,
  };
  const FOOTER_INFO: InfoBoxType = {
    type: window.PMW.emails.styles.ID_INFO_BOX,
    component: undefined,
    class: '-warning no-vertical-margin',
    content: window.i18next.t('pmwjs_email_footer_info'),
    isVisible: () => true,
  };
  const DESIGN_WARNING: InfoBoxType = {
    type: window.PMW.emails.styles.ID_INFO_BOX,
    component: undefined,
    class: '-warning no-vertical-margin',
    content: window.i18next.t('pmwjs_design_trimmed_warning'),
    isVisible: ({component}): boolean => {
      const componentSrc = component?.get('src') as string;
      const src: string = componentSrc?.split('?')[0];

      if (!src) return false;

      const segments = src.split('.');
      const ext = segments[segments.length - 1];
      return ext === 'gif';
    },
  };

  const SAVE_ICON_BUTTON: IconButtonType = {
    property: window.PMW.emails.commands.SAVE_USER_COMPONENT_TEMPLATE,
    type: 'pmw-icon-button',
    component: undefined,
    iconClass: 'icon-bookmark',
    description: window.i18next.t('pmwjs_save'),
    callback: (saveButton): void => {
      const button = $(saveButton);

      if (button.hasClass('-selected')) {
        return;
      }

      const icon = button.find('.pmw-icon-button-icon');
      const text = button.find('.pmw-icon-button-text');

      button.addClass('-selected');
      icon.removeClass('icon-bookmark').addClass('icon-loader gear-clockwise-animation -selected');
      text.html(window.i18next.t('pmwjs_saving'));

      editor.runCommand(window.PMW.emails.commands.SAVE_USER_COMPONENT_TEMPLATE, {
        component: editor.getSelected(),
        callback: () => {
          icon.removeClass('icon-loader gear-clockwise-animation').addClass('icon-bookmark-filled');
          text.html(window.i18next.t('pmwjs_saved'));
        },
      });
    },
  };
  const DUPLICATE_ICON_BUTTON: IconButtonType = {
    property: window.PMW.emails.commands.DUPLICATE_COMPONENT,
    type: 'pmw-icon-button',
    component: undefined,
    iconClass: 'icon-copy',
    description: window.i18next.t('pmwjs_duplicate'),
    callback: (): void => {
      editor.runCommand(window.PMW.emails.commands.DUPLICATE_COMPONENT);
    },
  };
  const DELETE_ICON_BUTTON: IconButtonType = {
    property: 'delete',
    type: 'pmw-icon-button',
    component: undefined,
    class: 'delete-color',
    iconClass: 'icon-delete',
    description: window.i18next.t('pmwjs_delete'),
    callback: (): void => {
      const styleManagerEl = $('.html-editor-style-manager');
      const isActive = styleManagerEl.hasClass('-active');

      if (!isActive) {
        return;
      }

      editor.runCommand(window.PMW.emails.commands.REMOVE_COMPONENT, {component: editor.getSelectedAll()});
    },
  };
  const CLEAR_SELECTION_ICON_BUTTON: IconButtonType = {
    property: 'clear-selection',
    type: 'pmw-icon-button',
    class: 'clear-selection',
    component: undefined,
    iconClass: 'icon-clear-selection',
    description: window.i18next.t('pmwjs_clear_selection'),
    callback: (): void => {
      editor.runCommand(window.PMW.emails.commands.SET_DEFAULT_SELECTION, {});
    },
  };
  const LOCK_ICON_BUTTON: IconButtonType = {
    property: 'lock',
    type: 'pmw-icon-button',
    component: undefined,
    iconClass: 'icon-lock',
    description: window.i18next.t('pmwjs_lock'),
    callback: (el: HTMLElement): void => {
      editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOCK, {el});
    },
  };
  const AI_REWRITE_PROMPT_BOX: AiRewritePromptBoxType = {
    type: window.PMW.emails.styles.ID_AI_REWRITE_PROMPT_BOX,
    component: undefined,
    label: '',
  };

  const SINGLE_COMPONENT_AI_REWRITE_PROMPT_BOX: AiRewritePromptBoxType = {
    type: window.PMW.emails.styles.ID_SINGLE_COMPONENT_AI_REWRITE_PROMPT_BOX,
    component: undefined,
    label: '',
  };

  const VIDEO_MODIFICATION_LIST: MediaModificationListType = {
    type: window.PMW.emails.styles.ID_MOD_LIST,
    component: undefined,
    class: 'media-modification',
    list: [
      {
        iconClass: 'icon-replace',
        label: window.i18next.t('pmwjs_replace'),
        subOptions: [
          {
            label: window.i18next.t('pmwjs_myuploads'),
            icon: 'icon-upload',
            cb: (): void => {
              editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_MY_UPLOADS_VIDEO);
            },
          },
          {
            label: window.i18next.t('pmwjs_explore_media'),
            icon: 'icon-media',
            cb: (): void => {
              editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_EXPLORE_MEDIA_VIDEO);
            },
          },
          {
            label: window.i18next.t('pmwjs_embed_videos'),
            icon: 'icon-html',
            cb: (): void => {
              editor.runCommand(window.PMW.emails.commands.mediaModals.OPEN_EMBED_VIDEO);
            },
          },
        ],
        callback: (_editorInstance: Editor, _component: Component, _li: HTMLElement, modList: HTMLElement): void => {
          const modListContainer = modList.querySelector('.mod-list-container');
          const modListSuboptions = modList.querySelector('.mod-list-suboptions-container');
          modListContainer?.classList.add('_hidden');
          modListSuboptions?.classList.remove('_hidden');
        },
      },
    ],
  };

  const MEDIA_MODIFICATION_LIST: MediaModificationListType = {
    type: window.PMW.emails.styles.ID_MOD_LIST,
    component: undefined,
    class: 'media-modification',
    list: [
      {
        property: window.PMW.emails.props.CROP_DATA,
        iconClass: 'icon-crop',
        label: window.i18next.t('pmwjs_crop'),
        callback: (_editorInstance: Editor, component: Component): void => {
          editor.runCommand(window.PMW.emails.commands.OPEN_CROPPER, {component});
        },
      },
      imageAndDesignReplacementOptions,
      {
        iconClass: 'icon-remove-bg btn-link',
        label: window.i18next.t('pmwjs_ai_background_remover'),
        action: window.PMW.emails.commands.REMOVE_BACKGROUND,
        credits: undefined,
        callback: (editorInstance: Editor, component: Component, el: HTMLElement): void => {
          editorInstance.runCommand(window.PMW.emails.commands.REMOVE_BACKGROUND, {
            component,
            el,
          });
        },
      },
      {
        iconClass: 'icon-ai-image',
        label: window.i18next.t('pmwjs_ai_images'),
        action: window.PMW.emails.commands.mediaModals.OPEN_AI_IMAGES,
        callback: (editorInstance: Editor): void => {
          editorInstance.runCommand(window.PMW.emails.commands.mediaModals.OPEN_AI_IMAGES);
        },
      },
    ],
  };

  const DESIGN_MODIFICATIONS_LIST = {
    type: window.PMW.emails.styles.ID_MOD_LIST,
    component: undefined,
    class: 'design-modification',
    list: [
      {
        iconClass: 'icon-pencil',
        label: window.i18next.t('pmwjs_edit_design'),
        callback: (_editorInstance: Editor, component: Component): void => {
          const id = component?.get(window.PMW.emails.props.DESIGN_ID) as string;
          const creatorID = component?.get(window.PMW.emails.props.DESIGN_CREATOR_ID) as string;
          if (!id) {
            window.PMW.openMessageModal({
              title: window.i18next.t('pmwjs_no_design_added'),
              text: window.i18next.t('pmwjs_select_a_design_error'),
            });
            return;
          }

          editor.runCommand(window.PMW.emails.commands.OPEN_POSTERMAKER, {id, creatorID});
          // Tracking temporarily disabled on 8th Nov 2023
          // editor.runCommand(window.PMW.emails.commands.TRACK_GA_EVENT_COMPONENT, {
          //     component: component,
          //     eventAction: 'EmailMaker_Edit'
          // });
        },
      },
      imageAndDesignReplacementOptions,
    ],
  };

  const URL_WRAPPER_TEXT = {
    property: 'url',
    type: window.PMW.emails.styles.ID_LINK_WRAPPER,
    component: undefined,
  };
  const SOCIAL_LINKS = {
    property: 'social-link',
    type: window.PMW.emails.styles.ID_SOCIAL_LINKS,
    component: undefined,
  };
  const styleHeader = (text: string, classes = ''): StyleHeading => {
    return editor.runCommand(window.PMW.emails.commands.ADD_STYLE_HEADER, {text, classes}) as StyleHeading;
  };

  const NAME_INPUT = {
    type: 'pmw-template-name',
    property: window.PMW.emails.props.TEMPLATE_NAME,
    component: undefined,
    label: window.i18next.t('pmwjs_name'),
    cb: undefined,
  };

  const isWhitelistedUserForAdminFeatures = (): boolean => {
    const userId = getUserId();

    if (userId && adminFeaturesUserIdWhiteList.includes(userId)) {
      return true;
    }

    return false;
  };

  editor.on('component:selected', async (component: Component) => {
    if (window.PMW.emails.IS_IN_VERSION_HISTORY_MODE) {
      return;
    }

    const styleManager = editor.StyleManager;
    const style = component.getStyle();
    styleManager.getSectors().reset();

    if (!Object.prototype.hasOwnProperty.call(SU_OPTS, 'isSuper')) {
      SU_OPTS.isSuper = await isSuperUser();
    }

    const allSliders = [
      widthSliderVO,
      limitedWidthSliderVO,
      paddingSliderVO,
      verticalMarginSliderVO,
      borderRadiusSliderVO,
      borderWidthSliderVO,
      opacitySliderVO,
      fontSizeSliderVO,
      lineHeightSliderVO,
      letterSpacingSliderVO,
      heightSliderVO,
      socialIconGapSliderVO,
      socialIconSizeSliderVO,
    ];
    const allStepButtons = [rowStepButtons, columnsStepButtons];
    const allStyleObjects = [
      COLUMNS_SELECTOR,
      PRESET_SELECTOR,
      WIDTH_DROPDOWN_SELCTOR,
      HORIZONTAL_ALIGN,
      TEXT_ALIGN,
      TEXT_ALIGN_SIMPLE,
      VERTICAL_ALIGN,
      FONT_STYLE_OPTIONS,
      TEXT_COLOR,
      BG_COLOR,
      BORDER_COLOR,
      LINK_COLOR,
      CELLS_BG_COLOR,
      CELLS_BORDER_COLOR,
      BACKGROUND_MEDIA,
      FONT_FAMILY,
      VIDEO_WARNING,
      DESIGN_WARNING,
      SAVE_ICON_BUTTON,
      DUPLICATE_ICON_BUTTON,
      DELETE_ICON_BUTTON,
      LOCK_ICON_BUTTON,
      CLEAR_SELECTION_ICON_BUTTON,
      MEDIA_MODIFICATION_LIST,
      DESIGN_MODIFICATIONS_LIST,
      URL_WRAPPER_TEXT,
      SOCIAL_LINKS,
      NAME_INPUT,
      EMAIL_TEMPLATE_STYLES,
      AI_REWRITE_PROMPT_BOX,
      BORDER_SELECTOR,
      MEGRE_TAGS_STYLES,
    ];

    allSliders.map((slider) => {
      slider.setDefault(component);
    });

    allStepButtons.map((stepButtons) => {
      stepButtons.setDefault(component);
    });

    allStyleObjects.map((object) => {
      object.component = component;
      return object;
    });

    if (style) {
      if (style['text-align']) {
        TEXT_ALIGN.defaults = style['text-align'] as string;
        TEXT_ALIGN_SIMPLE.defaults = style['text-align'] as string;
      }

      if (style['vertical-align']) {
        VERTICAL_ALIGN.defaults = style['vertical-align'] as string;
      }

      const componentAlignment = editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_HORIZONTAL_ALIGNMENT, {component}) as ComponentAlignmentStyle | undefined;
      if (componentAlignment) {
        HORIZONTAL_ALIGN.defaults = componentAlignment;
      }
    }

    let sectorTitle = component.get('name') ?? '';

    if (editor.runCommand(window.PMW.emails.commands.IS_MULTISELECT)) {
      sectorTitle = window.i18next.t('pmwjs_group');

      styleManager.addSector(
        'pmw-group-select',
        {
          name: sectorTitle,
          open: true,
          properties: [DUPLICATE_ICON_BUTTON, DELETE_ICON_BUTTON, CLEAR_SELECTION_ICON_BUTTON],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.table.PMW_TABLE) {
      styleManager.addSector(
        'pmw-table-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            DUPLICATE_ICON_BUTTON,
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            rowStepButtons.toObject() as PropertyTypes,
            columnsStepButtons.toObject() as PropertyTypes,
            styleHeader(window.i18next.t('pmwjs_cell_styles'), 'spacing-m-t-6'),
            CELLS_BG_COLOR,
            cellBorderWidthSliderVO.toObject() as PropertyTypes,
            CELLS_BORDER_COLOR,
            cellBorderRadiusSliderVO.toObject() as PropertyTypes,
            cellSpacingSliderVO.toObject() as PropertyTypes,
            styleHeader(window.i18next.t('pmwjs_table_styles'), 'spacing-m-t-6'),
            BACKGROUND_MEDIA,
            BG_COLOR,
            VERTICAL_ALIGN,
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            paddingSliderVO.toObject() as PropertyTypes,
          ],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.misc.PMW_READ_ONLY_FOOTER) {
      styleManager.addSector(
        'pmw-section-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [FOOTER_INFO],
        },
        {at: 0}
      );
    } else if (component.get('type') === 'table' || component.get('type') === window.PMW.emails.components.table.PMW_SECTION) {
      styleManager.addSector(
        'pmw-section-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            SAVE_ICON_BUTTON,
            DUPLICATE_ICON_BUTTON,
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            styleHeader(window.i18next.t('pmwjs_layout'), 'spacing-m-t-6'),
            COLUMNS_SELECTOR,
            styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
            BACKGROUND_MEDIA,
            BG_COLOR,
            VERTICAL_ALIGN,
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            columnSpacingSliderVO.toObject() as PropertyTypes,
            paddingSliderVO.toObject() as PropertyTypes,
          ],
        },
        {at: 0}
      );
      if (SU_OPTS.isSuper) {
        styleManager.addSector('pmw-super-section-sector', {
          name: sectorTitle,
          open: true,
          properties: [styleHeader('Admin component controls'), SU_SAVE_AS_SECTION_TEMPLATE, SU_COMPONENT_TEST, SU_COMPONENT_LOG_PROPS, SU_COMPONENT_REMOVE, SU_PARENT_REMOVE],
        });
      }
    } else if (component.get('type') === window.PMW.emails.components.social.PMW_SOCIALS_ITEM) {
      styleManager.addSector(
        'pmw-socials-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [LOCK_ICON_BUTTON, DELETE_ICON_BUTTON],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.social.PMW_SOCIALS) {
      styleManager.addSector(
        'pmw-div-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            SOCIAL_LINKS,
            styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
            TEXT_ALIGN_SIMPLE,
            ICON_STYLE,
            socialIconGapSliderVO.toObject(),
            socialIconSizeSliderVO.toObject(),
            BG_COLOR,
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            paddingSliderVO.toObject() as PropertyTypes,
          ],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.table.PMW_STATIC_CELL || component.get('type') === window.PMW.emails.components.misc.PMW_DIV) {
      sectorTitle = window.i18next.t('pmwjs_static_section');
      styleManager.addSector(
        'pmw-div-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            DUPLICATE_ICON_BUTTON,
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            paddingSliderVO.toObject(),
            BG_COLOR,
            widthSliderVO.toObject(),
            borderRadiusSliderVO.toObject(),
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
          ],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.table.PMW_CELL) {
      styleManager.addSector(
        'pmw-cell-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            styleHeader(window.i18next.t('pmwjs_styles')),
            COPY_PASTE_STYLES,
            BACKGROUND_MEDIA,
            BG_COLOR,
            VERTICAL_ALIGN,
            widthSliderVO.toObject(),
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            paddingSliderVO.toObject(),
          ],
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.misc.PMW_DIVIDER || component.get('type') === window.PMW.emails.components.misc.PMW_DIVIDER_LEGACY) {
      styleManager.addSector(
        'pmw-divider-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            DUPLICATE_ICON_BUTTON,
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
            COPY_PASTE_STYLES,
            BG_COLOR,
            widthSliderVO.toObject() as PropertyTypes,
            heightSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            HORIZONTAL_ALIGN,
          ],
        },
        {at: 0}
      );
    } else if (component.is(window.PMW.emails.components.container.PMW_WRAPPER)) {
      const emailBgColor = {...BG_COLOR};
      const contentBgColor = {...BG_COLOR};
      const emailPaddingSlider = paddingSliderVO.toObject();
      const contentContainer = editor.getWrapper()?.findType(window.PMW.emails.components.container.PMW_CONTENT)[0];

      emailBgColor.isDeletable = false;
      emailBgColor.label = window.i18next.t('pmwjs_email_background');
      contentBgColor.label = window.i18next.t('pmwjs_content_background');

      if (contentContainer) {
        BORDER_SELECTOR.component = contentContainer;
        BORDER_COLOR.component = contentContainer;
        contentBgColor.component = contentContainer;

        paddingSliderVO.setDefault(contentContainer);
        borderWidthSliderVO.setDefault(contentContainer);
        borderRadiusSliderVO.setDefault(contentContainer);
        limitedWidthSliderVO.setDefault(contentContainer);
      }

      const properties: PropertyTypes[] = [
        NAME_INPUT,
        EMAIL_TEMPLATE_STYLES,
        styleHeader(window.i18next.t('pmwjs_email'), 'spacing-m-t-6'),
        emailBgColor,
        emailPaddingSlider,
        styleHeader(window.i18next.t('pmwjs_content'), 'spacing-m-t-6'),
        contentBgColor,
        limitedWidthSliderVO.toObject(),
        BORDER_SELECTOR,
        BORDER_COLOR,
        borderWidthSliderVO.toObject() as PropertyTypes,
        borderRadiusSliderVO.toObject() as PropertyTypes,
        paddingSliderVO.toObject(),
      ];

      if (isEditorAiContentGenerationEnabled) {
        properties.splice(1, 0, AI_REWRITE_PROMPT_BOX);
      }

      sectorTitle = window.i18next.t('pmwjs_email');
      styleManager.addSector(
        'pmw-wrapper-sector',
        {
          name: sectorTitle,
          open: true,
          properties,
        },
        {at: 0}
      );

      if (SU_OPTS.isSuper || isWhitelistedUserForAdminFeatures()) {
        styleManager.addSector('pmw-super-global-sector', {
          name: 'pmw-global',
          open: true,
          properties: [
            styleHeader('Admin global controls', 'spacing-m-t-6'),
            SU_UPLOAD_CUSTOM_HTML,
            SU_IMAGE_INFO,
            SU_COPY_EMAIL_HTML_MARKETING,
            SU_COPY_EMAIL_TEXT_MARKETING,
            SU_COPY_EMAIL_MEDIA_QUERIES_MARKETING,
            COPY_TEXT_JSON,
            SU_OPEN_IN_ADMIN_PANEL,
            SU_DESKTOP_PREVIEW,
            SU_TABLET_PREVIEW,
            SU_PHONE_PREVIEW,
            SU_GLOBAL_TEST,
            SU_GLOBAL_LOG_JSON,
            SU_GLOBAL_SELECTION,
            SU_GLOBAL_AUTOSAVE,
            SU_GLOBAL_EXPOSE_EDITOR,
            SU_FIX_WRAPPER_WIDTH,
          ],
        });
      }
    } else if (component.is(window.PMW.emails.components.graphic.PMW_DESIGN)) {
      const imgWidthSliderVO = widthSliderVO.toObject();
      imgWidthSliderVO.isResponsive = true;
      styleManager.addSector(
        'pmw-my-design-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [
            DUPLICATE_ICON_BUTTON,
            LOCK_ICON_BUTTON,
            DELETE_ICON_BUTTON,
            DESIGN_WARNING,
            DESIGN_MODIFICATIONS_LIST,
            URL_WRAPPER_TEXT,
            styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
            COPY_PASTE_STYLES,
            opacitySliderVO.toObject(),
            imgWidthSliderVO,
            HORIZONTAL_ALIGN,
            BORDER_SELECTOR,
            BORDER_COLOR,
            borderWidthSliderVO.toObject() as PropertyTypes,
            borderRadiusSliderVO.toObject() as PropertyTypes,
            paddingSliderVO.toObject(),
          ],
        },
        {at: 0}
      );
    } else if (component.get(window.PMW.emails.props.IS_MEDIA) as boolean) {
      const isVideo = component.is(window.PMW.emails.components.graphic.PMW_VIDEO);
      const mediaModificationList = isVideo ? VIDEO_MODIFICATION_LIST : MEDIA_MODIFICATION_LIST;
      const imgWidthSliderVO = widthSliderVO.toObject();
      const topBarCommonButtons = [DUPLICATE_ICON_BUTTON, LOCK_ICON_BUTTON, DELETE_ICON_BUTTON];

      const indexToAddVideoWarning = topBarCommonButtons.length;
      const properties = [
        ...topBarCommonButtons,
        mediaModificationList,
        URL_WRAPPER_TEXT,
        styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
        COPY_PASTE_STYLES,
        opacitySliderVO.toObject(),
        imgWidthSliderVO,
        HORIZONTAL_ALIGN,
        BORDER_SELECTOR,
        BORDER_COLOR,
        borderWidthSliderVO.toObject() as PropertyTypes,
        borderRadiusSliderVO.toObject() as PropertyTypes,
        paddingSliderVO.toObject(),
      ];

      imgWidthSliderVO.isResponsive = true;

      if (isVideo) {
        // eslint-disable-next-line
        // @ts-ignore
        properties.splice(indexToAddVideoWarning, 0, VIDEO_WARNING);
      }

      styleManager.addSector(
        'pmw-media-sector',
        {
          name: sectorTitle,
          open: true,
          properties,
        },
        {at: 0}
      );
    } else if (component.get('type') === window.PMW.emails.components.text.PMW_BUTTON) {
      const widthSlider = widthSliderVO.toObject();
      widthSlider.isResponsive = true;
      widthSlider.propertyLabel = window.i18next.t('pmwjs_value');

      const properties = [
        DUPLICATE_ICON_BUTTON,
        LOCK_ICON_BUTTON,
        DELETE_ICON_BUTTON,
        URL_WRAPPER_TEXT,
        styleHeader(window.i18next.t('pmwjs_shape'), 'spacing-m-t-6'),
        PRESET_SELECTOR,
        styleHeader(window.i18next.t('pmwjs_button_styles'), 'spacing-m-t-6'),
        COPY_PASTE_STYLES,
        opacitySliderVO.toObject(),
        BG_COLOR,
        WIDTH_DROPDOWN_SELCTOR,
        widthSlider,
        HORIZONTAL_ALIGN,
        BORDER_SELECTOR,
        BORDER_COLOR,
        borderWidthSliderVO.toObject() as PropertyTypes,
        paddingSliderVO.toObject(),
        styleHeader(window.i18next.t('pmwjs_text_styles'), 'spacing-m-t-6'),
        TEXT_COLOR,
        FONT_FAMILY,
        fontSizeSliderVO.toObject(),
        FONT_STYLE_OPTIONS,
        TEXT_ALIGN,
        lineHeightSliderVO.toObject(),
        letterSpacingSliderVO.toObject(),
      ];

      if (isEditorAiContentGenerationEnabled) {
        properties.splice(BUTTON_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX, 0, SINGLE_COMPONENT_AI_REWRITE_PROMPT_BOX);
      }

      styleManager.addSector(
        'pmw-button-sector',
        {
          name: sectorTitle,
          open: true,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          properties,
        },
        {at: 0}
      );
    } else if (
      component.get('type') === window.PMW.emails.components.text.PMW_TEXT ||
      component.get('type') === 'text' ||
      (component.get('tagName')?.toLowerCase() === 'a' && component.get('type') !== window.PMW.emails.components.text.PMW_BUTTON)
    ) {
      const properties = [
        DUPLICATE_ICON_BUTTON,
        LOCK_ICON_BUTTON,
        DELETE_ICON_BUTTON,
        URL_WRAPPER_TEXT,
        MEGRE_TAGS_STYLES,
        styleHeader(window.i18next.t('pmwjs_styles'), 'spacing-m-t-6'),
        COPY_PASTE_STYLES,
        BG_COLOR,
        TEXT_COLOR,
        LINK_COLOR,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        FONT_FAMILY,
        fontSizeSliderVO.toObject(),
        FONT_STYLE_OPTIONS,
        TEXT_ALIGN,
        HORIZONTAL_ALIGN,
        lineHeightSliderVO.toObject(),
        letterSpacingSliderVO.toObject(),
        opacitySliderVO.toObject(),
        widthSliderVO.toObject(),
        paddingSliderVO.toObject(),
      ];

      if (isEditorAiContentGenerationEnabled) {
        properties.splice(TEXT_COMPONENT_AI_REWRITE_PROMPT_BOX_INDEX, 0, SINGLE_COMPONENT_AI_REWRITE_PROMPT_BOX);
      }

      sectorTitle = window.i18next.t('pmwjs_text');
      styleManager.addSector(
        'pmw-text-sector',
        {
          name: sectorTitle,
          open: true,
          properties: properties as PropertyTypes[],
        },
        {at: 0}
      );
    } else {
      sectorTitle = component.get('type') ?? '';
      styleManager.addSector(
        'pmw-general-sector',
        {
          name: sectorTitle,
          open: true,
          properties: [DUPLICATE_ICON_BUTTON, LOCK_ICON_BUTTON, DELETE_ICON_BUTTON],
        },
        {at: 0}
      );
    }

    if (SU_OPTS.isSuper && !(component.is(window.PMW.emails.components.container.PMW_WRAPPER) || component.is(window.PMW.emails.components.table.PMW_SECTION))) {
      styleManager.addSector('pmw-super-comp-sector', {
        name: sectorTitle,
        open: true,
        properties: [styleHeader('Admin component controls', 'spacing-m-t-6'), SU_COMPONENT_TEST, SU_COMPONENT_LOG_PROPS, SU_COMPONENT_REMOVE, SU_PARENT_REMOVE],
      });
    }

    editor.runCommand(window.PMW.emails.commands.INIT_COLLAPSED_STYLES);
    editor.runCommand(window.PMW.emails.commands.INIT_DROPDOWN_SUBOPTIONS);
    editor.runCommand(window.PMW.emails.commands.INIT_STYLE_FOR_LINK_COLOR, {component});
    editor.runCommand(window.PMW.emails.commands.TOGGLE_STYLEMANAGER_SCROLL_LISTENER, {toggleOn: true});

    updateSectorTitle(sectorTitle);
    setTimeout(() => {
      window.initResponsiveToolTips();
    }, 100); // let stylemanager and toolbar render before initiating tooltips
    hidePropertiesForApprovedTemplates(editor);
    hideTitleForWrapperSector(component);
  });
};
